namespace $.$$ {

	type $shm_hitalama_map_widget_float_group_offset = {
		top: number
		left: number
	}

	type $shm_hitalama_map_widget_float_group_rect = {
		top: number
		left: number
		width: number
		height: number
	}

	type $shm_hitalama_map_widget_float_group_bounds = {
		top: number
		left: number
		right: number
		bottom: number
	}

	export class $shm_hitalama_map_widget_float_group extends $.$shm_hitalama_map_widget_float_group {

		relatives?: readonly $shm_hitalama_map_widget_float_group_offset[]
		resize_relatives?: readonly $shm_hitalama_map_widget_float_group_rect[]
		widgets_layouting = false

		edges(): readonly any[] {
			return super.edges()
		}

		some_inner_transforming() {
			const page = this.Map_page()
			return this.widget().widgets().some( w => page.Widget( w.link() ).Sub().transforming() )
		}

		count() {
			return this.widget().z_descendants().length
		}

		count_title() {
			return String( this.count() )
		}

		@ $mol_mem
		transition() {
			if( this.some_inner_transforming() ) return 'none'
			return super.transition()
		}

		@ $mol_mem
		transforming() {
			const transforming = super.transforming()
			return Boolean( this.resize_relatives ) || transforming
		}

		@ $mol_action
		fit_to_widgets() {
			const bounds = this.widgets_bounds()
			if( !bounds ) return
			this.fit_to_bounds( bounds )
		}

		@ $mol_action
		fit_to_bounds( bounds: $shm_hitalama_map_widget_float_group_bounds ) {
			super.top( bounds.top )
			super.left( bounds.left )
			super.width( bounds.right - bounds.left )
			super.height( bounds.bottom - bounds.top )
		}

		@ $mol_mem
		widgets_bounds(): $shm_hitalama_map_widget_float_group_bounds | null {
			const widgets = this.widget().widgets()
			if( widgets.length === 0 ) return null

			const page = this.Map_page()
			return widgets.reduce( ( acc, w )=> {
				const widget = page.Widget( w.link() ).Sub()!
				const left = widget.left()
				const top = widget.top()
				const right = left + widget.width()
				const bottom = top + widget.height()

				return {
					left: Math.min( acc.left, left ),
					top: Math.min( acc.top, top ),
					right: Math.max( acc.right, right ),
					bottom: Math.max( acc.bottom, bottom ),
				}
			}, {
				left: Infinity,
				top: Infinity,
				right: -Infinity,
				bottom: -Infinity,
			} )
		}

		@ $mol_mem
		fit_to_widgets_auto() {
			if( this.widgets_layouting ) return null
			if( super.transforming() || this.resize_relatives ) return null

			const bounds = this.widgets_bounds()
			if( bounds ) this.fit_to_bounds( bounds )
			return null
		}

		@ $mol_action
		set_relatives() {
			this.fit_to_widgets()
		}

		resize_start( next?: any ) {
			this.resize_relatives = this.calc_relative_rects()
			super.resize_start( next )
		}

		resize_end() {
			const top = this.top()
			const left = this.left()
			const width = this.width()
			const height = this.height()

			super.resize_end()
			this.resize_relatives = undefined

			this.x_stick( left )
			this.y_stick( top )
			this.left_edge_x_stick( 0 )
			this.top_edge_y_stick( 0 )
			this.right_edge_x_stick( width )
			this.bottom_edge_y_stick( height )
		}

		@ $mol_mem
		x( next?: number ): number {
			if( next === undefined ) return this.left()
			return this.left( next )
		}

		@ $mol_mem
		y( next?: number ): number {
			if( next === undefined ) return this.top()
			return this.top( next )
		}

		@ $mol_mem
		top_edge_y( next?: number ): number {
			if( next === undefined ) return this.top_edge_y_stick() ?? 0
			const result = super.top_edge_y( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		bottom_edge_y( next?: number ): number {
			if( next === undefined ) return this.bottom_edge_y_stick() ?? 0
			const result = super.bottom_edge_y( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		left_edge_x( next?: number ): number {
			if( next === undefined ) return this.left_edge_x_stick() ?? 0
			const result = super.left_edge_x( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		right_edge_x( next?: number ): number {
			if( next === undefined ) return this.right_edge_x_stick() ?? 0
			const result = super.right_edge_x( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		top_edge_y_stick( next?: number ) {
			const result = super.top_edge_y_stick( next )
			if( next !== undefined && this.resize_relatives ) this.resize_widgets()
			return result
		}

		@ $mol_mem
		bottom_edge_y_stick( next?: number ) {
			const result = super.bottom_edge_y_stick( next )
			if( next !== undefined && this.resize_relatives ) this.resize_widgets()
			return result
		}

		@ $mol_mem
		left_edge_x_stick( next?: number ) {
			const result = super.left_edge_x_stick( next )
			if( next !== undefined && this.resize_relatives ) this.resize_widgets()
			return result
		}

		@ $mol_mem
		right_edge_x_stick( next?: number ) {
			const result = super.right_edge_x_stick( next )
			if( next !== undefined && this.resize_relatives ) this.resize_widgets()
			return result
		}

		on_drag_start( event: PointerEvent ) {
			this.relatives = this.calc_relative_coords( this.top(), this.left() )
			super.on_drag_start( event )
		}

		on_drag( event: PointerEvent ) {
			this.move_widgets()
			super.on_drag( event )
		}

		on_drag_end( event: PointerEvent ) {
			super.on_drag_end( event )
			this.relatives = undefined
		}

		ungroup() {
			const widgets = this.widget().widgets()
			const ungroupped: $giper_baza_link[] = []

			widgets.forEach( w => {
				w.container_widget( null )
				ungroupped.push( w.link() )
			} )

			this.widget().widgets( [] )
			this.map().widget_cut( this.widget().link() )
			this.Map_page().selected_links( ungroupped )
		}

		@ $mol_mem
		top( next?: number ): number {
			if( next === undefined ) return super.top()

			const relatives = this.calc_relative_coords( this.top(), this.left() )
			const top = super.top( next )
			this.move_widgets( relatives, top, this.left() )
			return top
		}

		@ $mol_mem
		left( next?: number ): number {
			if( next === undefined ) return super.left()

			const relatives = this.calc_relative_coords( this.top(), this.left() )
			const left = super.left( next )
			this.move_widgets( relatives, this.top(), left )
			return left
		}

		@ $mol_mem
		width( next?: number ): number {
			if( next === undefined ) return super.width()

			const relatives = this.calc_relative_rects()
			const width = super.width( next )
			this.resize_widgets( relatives )
			return width
		}

		@ $mol_mem
		height( next?: number ): number {
			if( next === undefined ) return super.height()

			const relatives = this.calc_relative_rects()
			const height = super.height( next )
			this.resize_widgets( relatives )
			return height
		}

		move_widgets( relatives = this.relatives, top = this.top(), left = this.left() ) {
			if( !relatives ) return

			const widgets = this.widget().widgets()
			const page = this.Map_page()
			this.widgets_layouting = true
			try {
				for( let i = 0; i < widgets.length; i++ ) {
					const w = widgets[i]
					const widget = page.Widget( w.link() ).Sub()!
					const coords = relatives[i]
					if( !coords ) continue
					widget.top( top + coords.top )
					widget.left( left + coords.left )
				}
			} finally {
				this.widgets_layouting = false
			}
		}

		resize_widgets( relatives = this.resize_relatives ) {
			if( !relatives ) return

			const top = this.top()
			const left = this.left()
			const width = this.width()
			const height = this.height()
			const widgets = this.widget().widgets()
			const page = this.Map_page()

			this.widgets_layouting = true
			try {
				for( let i = 0; i < widgets.length; i++ ) {
					const w = widgets[i]
					const widget = page.Widget( w.link() ).Sub()!
					const rect = relatives[i]
					if( !rect ) continue

					widget.top( top + rect.top * height )
					widget.left( left + rect.left * width )
					widget.width( rect.width * width )
					widget.height( rect.height * height )
				}
			} finally {
				this.widgets_layouting = false
			}
		}

		calc_relative_coords( top: number, left: number ) {
			const widgets = this.widget().widgets()
			const page = this.Map_page()

			return widgets.map( w => {
				const widget = page.Widget( w.link() ).Sub()!
				return {
					top: widget.top() - top,
					left: widget.left() - left,
				}
			} )
		}

		calc_relative_rects() {
			const widgets = this.widget().widgets()
			const page = this.Map_page()
			const top = this.top()
			const left = this.left()
			const width = this.width() || 1
			const height = this.height() || 1

			return widgets.map( w => {
				const widget = page.Widget( w.link() ).Sub()!
				return {
					top: ( widget.top() - top ) / height,
					left: ( widget.left() - left ) / width,
					width: widget.width() / width,
					height: widget.height() / height,
				}
			} )
		}
		
	}
}
